import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { sendMemberUiEvent } from '../../lib/api/memberApi'
import { getLineContact } from '../../lib/lineContact'
import { GoldAura } from './GoldAura'
import { GoldBagIcon } from './GoldBagIcon'
import { HubCover } from './HubCover'
import { endLabel, lineOaMessageUrl, prizeInfo, type HubActivity } from './memberHubLogic'

/** ตำแหน่งปลายประกายที่ลอยขึ้น/ลงจากจุดต้นทาง (px) — คงที่เพื่อไม่ให้สุ่มใหม่ทุกครั้งที่ render */
const MOTES = [
  { mx: -34, my: 96, d: 0.05 }, { mx: 22, my: 128, d: 0.12 }, { mx: -8, my: 164, d: 0.2 },
  { mx: 40, my: 106, d: 0.28 }, { mx: -46, my: 140, d: 0.34 }, { mx: 6, my: 190, d: 0.4 },
]

/**
 * แสงจินนี่จากน้ำเต้า: แสงวาบ + ลำแสงทอง + ประกายลอย — ใช้ transform/opacity ล้วน (ลื่นบนมือถือ)
 * ตกแต่งล้วน (aria-hidden) · ซ่อนเมื่อลดการเคลื่อนไหว/ประหยัดพลังงาน (CSS)
 */
function GenieFx({ from, closing }: { from: { x: number; y: number }; closing: boolean }) {
  // การ์ดอยู่กลางจอ: ต้นทางอยู่ล่างกลางจอ = ลำแสงพุ่งขึ้น · ต้นทางอยู่บน = พุ่งลง
  const down = from.y < (typeof window !== 'undefined' ? window.innerHeight / 2 : 0)
  return (
    <span className={`mh-genie-fx${down ? ' mh-genie-fx--down' : ''}${closing ? ' is-closing' : ''}`} aria-hidden="true" style={{ left: from.x, top: from.y }}>
      <span className="mh-genie-flash" />
      <span className="mh-genie-beam" />
      {MOTES.map((m, i) => (
        <span key={i} className="mh-genie-mote" style={{ '--mx': `${m.mx}px`, '--my': `${down ? m.my : -m.my}px`, '--d': `${m.d}s` } as CSSProperties}>✦</span>
      ))}
    </span>
  )
}

/** รายการกิจกรรม — modal กลางจอในกรอบออร่าทอง · ร่วม/รับรางวัลยังผ่าน LINE ตามระบบเดิม */
export function ActivitySheet({ activities, botId, benefitsEligible, onProfileRequest, onClose, genieFrom = null }: { activities: HubActivity[]; botId: string; benefitsEligible: boolean; onProfileRequest: () => void; onClose: () => void; genieFrom?: { x: number; y: number } | null }) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const sheetRef = useRef<HTMLElement>(null)
  const lineId = getLineContact(botId).label
  const [closing, setClosing] = useState(false)
  const closingRef = useRef(false)
  // ระหว่างเล่นจินนี่ปิดเบลอของกระจกชั่วคราว (backdrop-filter ใต้ transform ทำให้กระตุก) แล้วเปิดคืนเมื่อจบ
  const [entering, setEntering] = useState(!!genieFrom)
  useEffect(() => {
    if (!genieFrom) return
    const timer = window.setTimeout(() => setEntering(false), 1000)
    return () => window.clearTimeout(timer)
  }, [genieFrom])
  // เล่นแอนิเมชันย่อ/จางก่อนปิดจริง · ลดการเคลื่อนไหว = ปิดทันที
  const requestClose = useCallback(() => {
    if (closingRef.current) return
    closingRef.current = true
    const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduce) { onClose(); return }
    setClosing(true)
    window.setTimeout(onClose, 240)
  }, [onClose])

  useEffect(() => {
    closeRef.current?.focus()
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { requestClose(); return }
      if (e.key !== 'Tab') return
      const controls = sheetRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href]')
      if (!controls?.length) return
      if (e.shiftKey && document.activeElement === controls[0]) { e.preventDefault(); controls[controls.length - 1].focus() }
      else if (!e.shiftKey && document.activeElement === controls[controls.length - 1]) { e.preventDefault(); controls[0].focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', onKey)
    }
  }, [requestClose])

  return (
    <div className={`mh-ov mh-ov--sheet${closing ? ' is-closing' : ''}`} onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose() }}>
      {genieFrom && <GenieFx from={genieFrom} closing={closing} />}
      <GoldAura closing={closing} genieFrom={genieFrom}>
        <section ref={sheetRef} className={`mh-sheet${closing ? ' is-closing' : ''}${entering ? ' is-entering' : ''}`} role="dialog" aria-modal="true" aria-labelledby="mh-sheet-title">
          <div className="mh-sheet-head">
            <h2 id="mh-sheet-title"><GoldBagIcon className="mh-sheet-bag" /> กิจกรรมสำหรับคุณ</h2>
            <button ref={closeRef} type="button" className="mh-icon-btn" onClick={requestClose} aria-label="ปิด"><i className="fas fa-xmark" aria-hidden="true" /></button>
          </div>
          {!benefitsEligible && <div className="mh-profile-required" role="note">
            <b>ยืนยันข้อมูลก่อนร่วมกิจกรรม</b>
            <p>กรอกชื่อ เบอร์โทร ที่อยู่ และวันเกิดปัจจุบันให้ครบ เพื่อเปิดสิทธิ์สมาชิก</p>
            <button type="button" className="mh-sbtn mh-sbtn--gold" onClick={onProfileRequest}>ยืนยันข้อมูลสมาชิก</button>
          </div>}
          {!activities.length && <div className="mh-empty-state"><p>ยังไม่มีกิจกรรมในถุงทองตอนนี้</p><span>เมื่อมีกิจกรรมสำหรับคุณ รายละเอียดจะปรากฏที่นี่</span></div>}
          {activities.map((g, i) => {
            const prize = prizeInfo(g)
            const keyword = g.keywords[0] || g.name
            return (
              <article key={g.id} className="mh-act" style={{ '--i': Math.min(i, 6) } as CSSProperties}>
                <HubCover cover={g.cover} wide />
                <div className="mh-act-body">
                  <div className="mh-act-title">
                    <h3>{g.name}</h3>
                    {g.isNew && <span className="mh-new">ใหม่</span>}
                  </div>
                  {g.description && <p>{g.description}</p>}
                  <div className="mh-chips">
                    {g.keywords.map((k) => <span key={k} className="mh-chip mh-chip--violet"><i className="fab fa-line" aria-hidden="true" /> พิมพ์ "{k}"</span>)}
                    {g.distribution && <span className="mh-chip">{g.distribution === 'random' ? 'สุ่มผู้โชคดี' : 'มาก่อนได้ก่อน'}</span>}
                    <span className={`mh-chip${prize?.low ? ' mh-chip--red' : ''}`}><i className="far fa-clock" aria-hidden="true" /> {endLabel(g.endAt)}</span>
                  </div>
                  {prize && (
                    <div className="mh-meter">
                      <div className="mh-meter-bar" role="progressbar" aria-label="สิทธิ์คงเหลือ" aria-valuenow={prize.pct} aria-valuemin={0} aria-valuemax={100}><i style={{ width: `${prize.pct}%` }} /></div>
                      <div className="mh-meter-text"><span>สิทธิ์คงเหลือ</span><span>{prize.text}</span></div>
                    </div>
                  )}
                  {benefitsEligible ? <a className="mh-sbtn mh-sbtn--line" href={lineOaMessageUrl(lineId, keyword)} target="_blank" rel="noopener noreferrer" onClick={() => { void sendMemberUiEvent({ event: 'activity_click', id: g.id }) }}>
                    <i className="fab fa-line" aria-hidden="true" /> ร่วมกิจกรรมใน LINE
                  </a> : <button type="button" className="mh-sbtn mh-sbtn--locked" onClick={onProfileRequest}><i className="fas fa-lock" aria-hidden="true" /> ยืนยันข้อมูลเพื่อรับสิทธิ์</button>}
                </div>
              </article>
            )
          })}
        </section>
      </GoldAura>
    </div>
  )
}
